// pdf.js (refonte optimisée)
import jsPDF from 'jspdf';

// === Assets bruts (URLs bundler) ===
import propositionJminterim from '../assets/images/proposition-jminterim.jpg';
import propositionCapeneo from '../assets/images/proposition-capaneo.jpg';
import logotoulouse from '../assets/images/logotoulouse.png';
import logobrive from '../assets/images/logobrive.png';
import myImage from '../assets/images/logopdf.png';
import gmail from '../assets/images/gmail.png';
import phone from '../assets/images/phone.png';
import cgpasnieres from '../assets/images/cgpasnieres.png';
import cgpbrive from '../assets/images/cgpbrive.png';
import cgptoulouse from '../assets/images/cgptoulouse.png';

// ========================= Utils =========================

// Découpe un texte sans couper les mots
function splitStringWithoutCuttingWords(inputString = '', chunkSize = 24) {
  const words = String(inputString || '').split(' ');
  const resultChunks = [];
  let current = '';
  for (const w of words) {
    if (!w) continue;
    if ((current ? current.length + 1 : 0) + w.length <= chunkSize) {
      current += (current ? ' ' : '') + w;
    } else {
      if (current) resultChunks.push(current);
      current = w;
    }
  }
  if (current) resultChunks.push(current);
  return resultChunks;
}

// Dates -> MMYYYY
const formatJsDate2 = (date) => {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}${year}`;
};

// Dates -> DD/MM/YYYY
const formatJsDate = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Charge une image (URL ou dataURL) et retourne un dataURL (avec resize/qualité)
async function loadAsDataURL(src, { type = 'image/jpeg', quality = 0.7, maxW, maxH } = {}) {
  // Si déjà en dataURL, on peut tout de même resizer si maxW/maxH fournis
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });

  // Pas de resize demandé -> retourne brut si déjà dataURL et type cohérent
  const needsResize = !!(maxW || maxH);
  const hasAlpha = type === 'image/png'; // png pour logos avec alpha

  if (!needsResize && String(src).startsWith('data:')) {
    return src;
  }

  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;

  let cw = w, ch = h;
  if (needsResize) {
    const MW = maxW || w;
    const MH = maxH || h;
    const ratio = Math.min(MW / w, MH / h, 1);
    cw = Math.max(1, Math.round(w * ratio));
    ch = Math.max(1, Math.round(h * ratio));
  }

  const c = document.createElement('canvas');
  c.width = cw; c.height = ch;
  const ctx = c.getContext('2d', { alpha: hasAlpha });
  ctx.drawImage(img, 0, 0, cw, ch);

  // Si type = jpeg et source contient de la transparence, le canvas va la perdre (ok pour fonds)
  return c.toDataURL(type, quality);
}

// Prépare & met en cache toutes les images utiles sous forme de dataURL optimisées
// - Pleine page: 150 DPI sur A4 → ~1240x1754
// - Logos/pictos: PNG pour alpha (pas de perte), pas besoin de grand
const A4_150DPI = { maxW: 1240, maxH: 1754 };

let _assetsCache = null;
export async function warmupPdfAssets() {
  if (_assetsCache) return _assetsCache;

  // Arrière-plans pleine page => JPEG compressé
  const [bgCapeneo, bgJminterim] = await Promise.all([
    loadAsDataURL(propositionCapeneo, { type: 'image/jpeg', quality: 0.65, ...A4_150DPI }),
    loadAsDataURL(propositionJminterim, { type: 'image/jpeg', quality: 0.65, ...A4_150DPI }),
  ]);

  // CGP pleine page => PNG (texte fin) mais on peut downscaler léger pour poids
  const [cgpToulouse, cgpBrive, cgpAsnieres] = await Promise.all([
    loadAsDataURL(cgptoulouse, { type: 'image/png', quality: 0.92, ...A4_150DPI }),
    loadAsDataURL(cgpbrive, { type: 'image/png', quality: 0.92, ...A4_150DPI }),
    loadAsDataURL(cgpasnieres, { type: 'image/png', quality: 0.92, ...A4_150DPI }),
  ]);

  // Logos/pictos => PNG (petites tailles)
  const [logoToulouse, logoBrive, logoDefault, iconGmail, iconPhone] = await Promise.all([
    loadAsDataURL(logotoulouse, { type: 'image/png', quality: 0.92, maxW: 600, maxH: 600 }),
    loadAsDataURL(logobrive, { type: 'image/png', quality: 0.92, maxW: 600, maxH: 600 }),
    loadAsDataURL(myImage, { type: 'image/png', quality: 0.92, maxW: 600, maxH: 600 }),
    loadAsDataURL(gmail, { type: 'image/png', quality: 0.92, maxW: 64, maxH: 64 }),
    loadAsDataURL(phone, { type: 'image/png', quality: 0.92, maxW: 64, maxH: 64 }),
  ]);

  _assetsCache = {
    bgCapeneo,
    bgJminterim,
    logoToulouse,
    logoBrive,
    logoDefault,
    iconGmail,
    iconPhone,
    cgpToulouse,
    cgpBrive,
    cgpAsnieres,
  };

  return _assetsCache;
}

// Sélecteurs d’assets selon l’agence
function pickBackground(agencyName, assets) {
  if (agencyName === 'CAPENEO' || agencyName === "J'M CAPENEO") return assets.bgCapeneo;
  return assets.bgJminterim;
}
function pickLogo(agencyName, assets) {
  if (agencyName === 'CAPENEO') return assets.logoToulouse;
  if (agencyName === "J'M CAPENEO") return assets.logoBrive;
  return assets.logoDefault;
}
function pickCGP(agencyName, assets) {
  if (agencyName === 'CAPENEO') return assets.cgpToulouse;
  if (agencyName === "J'M CAPENEO") return assets.cgpBrive;
  return assets.cgpAsnieres;
}

// ========================= Mise en page =========================

function addHeader(pdf, quotation, assets) {
  // Fond
  const bg = pickBackground(quotation.agency?.name, assets);
  // Alias 'bg' pour dédupliquer dans le PDF
  pdf.addImage(bg, 'JPEG', 0, 0, 210, 297, 'bg', 'MEDIUM');

  pdf.addPage();

  // Logo (alias 'logo')
  const logo = pickLogo(quotation.agency?.name, assets);
  if (quotation.agency?.name === 'CAPENEO') {
    pdf.addImage(logo, 'PNG', 14, 10, 40, 24, 'logo');
  } else if (quotation.agency?.name === "J'M CAPENEO") {
    pdf.addImage(logo, 'PNG', 24, 14, 32, 24, 'logo');
  } else {
    pdf.addImage(logo, 'PNG', 14, 10, 40, 24, 'logo');
  }

  // Coordonnées agence
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(String(quotation.agency?.name || ''), 24, 45);

  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(String(quotation.agency?.address || ''), 15, 50);
  pdf.text(`${quotation.agency?.postCode || ''} ${quotation.agency?.town || ''}`, 15, 55);
  pdf.text(`SIRET  : ${quotation.agency?.siret || ''}`, 15, 60);
  pdf.text(`TVA n° : ${quotation.agency?.tva || ''}`, 15, 65);

  pdf.addImage(assets.iconGmail, 'PNG', 15, 67, 3, 3, 'gmail');
  pdf.text(` : ${quotation.agency?.email || ''}`, 18, 69.5);

  pdf.addImage(assets.iconPhone, 'PNG', 15, 71.5, 3, 3, 'phone');
  pdf.text(` : ${quotation.agency?.phone || ''}`, 18, 74);

  const contactName =
    'Contact : ' +
    (quotation?.creator?.firstname?.charAt(0).toUpperCase() || '') +
    (quotation?.creator?.firstname?.slice(1) || '') +
    ' ' +
    (quotation?.creator?.lastname?.charAt(0).toUpperCase() || '') +
    (quotation?.creator?.lastname?.slice(1) || '');
  pdf.text(contactName, 15, 79.5);

  // Bloc devis
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(14);
  let quotationNumber =
    'Devis n° ' + formatJsDate2(quotation.createdAt) + '-' + String(quotation.quotationNumber ?? '').toString().padStart(5, '0');
  if (quotation.isDraft) quotationNumber = 'Devis provisoire';
  pdf.text(quotationNumber, 110, 45);

  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text('Date : ', 110, 50);
  pdf.text(formatJsDate(quotation.createdAt), 120, 50);
  pdf.text("Date d'expiration :", 110, 55);
  pdf.text(formatJsDate(quotation.expiryDate), 137, 55);

  // Adresse de facturation
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Adresse de facturation :', 15, 95);

  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(9);

  try {
    const match = String(quotation.addressFacturation || '')
      .trim()
      .match(/^(.*?)\b(\d{5})\b(.*)$/);

    if (match) {
      const [, partieAvant, codePostal, partieApres] = match.map((s) => String(s || '').trim());
      pdf.text(partieAvant, 15, 100);
      pdf.text(`${codePostal} ${partieApres}`, 15, 105);

      // Coord. client (droite)
      pdf.setFont('Helvetica', 'bold');
      pdf.setFontSize(12);
      const companyNameArray = splitStringWithoutCuttingWords(
        String(quotation?.client?.companyName || '').toUpperCase(),
        30
      );
      let size = 80 - (companyNameArray.length - 1) * 5;
      for (const name of companyNameArray) {
        pdf.text(name, 110, size);
        size += 5;
      }

      pdf.setFont('Helvetica', 'normal');
      pdf.setFontSize(9);
      let contact = String(quotation?.contact || '').split(' ');
      contact.pop();
      contact.pop();
      contact = contact.join(' ').trim();

      pdf.text(String(contact || '').toUpperCase(), 110.5, 85);
      pdf.text(partieAvant, 110.5, 90);
      pdf.text(`${codePostal} ${partieApres}`, 110.5, 95);
    }
  } catch (e) {
    // no-op
  }

  pdf.text(`SIRET : ${quotation?.client?.siret || ''}`, 110.5, 100);
  pdf.text(`TVA : ${quotation?.client?.TVA || ''}`, 110.5, 105);

  // Style bleu pour encadrés
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(9, 132, 227);
  pdf.setDrawColor(9, 132, 227);
}

function addIntroBoxes(pdf, quotation) {
  const type = quotation?.products?.[0]?.product;
  if (!type) return;

  if (type === 'Placement' || type === 'Vacation') {
    pdf.roundedRect(26, 115, 160, 30, 1, 1);
    pdf.text(type.toUpperCase(), 95, 120);
    pdf.line(95, 122, 122, 122);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    pdf.text(
      'Vous nous mandatez pour recruter au sein de votre entreprise, un poste en CDD ou CDI.',
      46,
      130
    );
    pdf.text(
      "Le contrat, la DPAE et la fiche de paie sont établis par vos soins. Vous êtes l'employeur.",
      46,
      135
    );
    pdf.text('Vous recevrez une seule facture pour le placement', 57, 140);
  }

  if (type === 'Délégation' || type === 'Gestion') {
    pdf.roundedRect(15, 110, 92, 35, 1, 1);
    pdf.roundedRect(110, 110, 92, 35, 1, 1);
    pdf.text('DELEGATION', 47, 115);
    pdf.line(47, 117, 75, 117);
    pdf.text('GESTION', 145, 115);
    pdf.line(145, 117, 164, 117);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    pdf.text('Vous nous mandatez pour', 17, 125);
    pdf.setFont('Helvetica', 'bold'); pdf.text('recruter', 55, 125);
    pdf.setFont('Helvetica', 'normal'); pdf.text('   et', 65, 125);
    pdf.setFont('Helvetica', 'bold'); pdf.text('  gérer', 70, 125);
    pdf.setFont('Helvetica', 'normal'); pdf.text(" l'administratif", 80, 125);
    pdf.text('de ', 17, 130);
    pdf.setFont('Helvetica', 'bold'); pdf.text('notre', 21, 130);
    pdf.setFont('Helvetica', 'normal');
    pdf.text('recrue. Le contrat, la DPAE et la fiche de paie sont', 30, 130);
    pdf.text("établis par nous, qui sommes l'employeur. Vous avez à charge", 17, 135);
    pdf.text('la sécurité du salarié. Vous recevrez une facture mensuelle.', 17, 140);

    pdf.text('Vous nous mandatez pour', 112, 125);
    pdf.setFont('Helvetica', 'bold'); pdf.text('gérer', 150, 125);
    pdf.setFont('Helvetica', 'normal'); pdf.text("simplement l'administratif", 159, 125);
    pdf.text('de', 112, 130);
    pdf.setFont('Helvetica', 'bold'); pdf.text('votre', 116, 130);
    pdf.setFont('Helvetica', 'normal');
    pdf.text('recrue. Le contrat, la DPAE et la fiche de paie sont', 125, 130);
    pdf.text("établis par nous, qui sommes l'employeur. Vous avez à charge", 112, 135);
    pdf.text('la sécurité du salarié. Vous recevrez une facture mensuelle.', 112, 140);
  }

  // Bandeau tableau
  pdf.setTextColor(0, 0, 0);
  pdf.setFillColor(9, 132, 227);
  pdf.setDrawColor(9, 132, 227);
  pdf.rect(15, 155, 180, 10, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(10);
}

function addTableAndTotals(pdf, quotation) {
  const type = quotation?.products?.[0]?.product;
  if (!type) return;

  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(10);

  if (type === 'Délégation' || type === 'Gestion') {
    let y = 170;

    pdf.text('Designation', 25, 161);
    pdf.text('Taux horaire', 60, 161);
    pdf.text('Coefficient', 95, 161);
    pdf.text('Prix HT/heure', 130, 159);
    pdf.text('travaillée', 135, 163);
    pdf.text('Observation', 165, 161);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.setDrawColor(0, 0, 0);

    for (let idx = 0; idx < quotation.products.length; idx++) {
      const p = quotation.products[idx];

      if (p.type === 'Prime' || p.type === 'Indemnité') {
        const label = (p.type === 'Prime' ? 'Prime : ' : 'Indemnité : ') + (p.product || '');
        const lines = splitStringWithoutCuttingWords(label, 24);
        for (const line of lines) { pdf.text(line, 20, y); y += 5; }
        y -= 5;
      } else if (p.type === 'Autre') {
        pdf.text(String(p.product || ''), 20, y);
      } else {
        pdf.text(`${p.product || ''} H/F`, 20, y);
        const poste = splitStringWithoutCuttingWords(p.poste || '', 24);
        for (const line of poste) { pdf.text(line, 20, y + 5); y += 5; }
      }

      const taux = p.taux !== '' ? parseFloat(p.taux) : NaN;
      const coef = p.coefficient !== '' ? parseFloat(p.coefficient) : NaN;

      if (!Number.isNaN(taux)) pdf.text(`${taux.toFixed(2)}€`, 65, y);
      if (!Number.isNaN(coef)) pdf.text(String(coef), 105, y);
      if (!Number.isNaN(taux) && !Number.isNaN(coef)) {
        pdf.text((taux * coef).toFixed(2) + '€', 140, y);
      }

      const commentLines = splitStringWithoutCuttingWords(String(p.comment || ''), 24);
      for (const line of commentLines) { pdf.text(line, 165, y); y += 5; }

      y += 5;
      pdf.setTextColor(128, 128, 128);
      pdf.setDrawColor(128, 128, 128);

      if (p.minimumHours) {
        pdf.text(
          "Nombre d'heures minimales avant embauche sans facturation : " + p.minimumHours + 'h',
          50,
          y
        );
        y += 3;
      }

      if (idx === quotation.products.length - 1) {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
      }
      pdf.line(15, y, 195, y);
      pdf.setTextColor(0, 0, 0);
      pdf.setDrawColor(0, 0, 0);
      y += 7;

      if (y > 260) { pdf.addPage(); y = 10; }
    }

    y += 10;
    if (y > 250) { pdf.addPage(); y = 10; }

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(9);

    pdf.text('Nom et qualité du signataire :', 15, y);
    pdf.text('Date : ' + formatJsDate(new Date()), 15, y + 5);

    pdf.text(String(quotation?.client?.tvaRate || ''), 120, y);
    pdf.text('Conditions de paiement : ' + String(quotation?.client?.facturation || ''), 120, y + 5);
    pdf.text('Moyen de règlement : Virement/Chèque/CB', 120, y + 10);
  }

  if (type === 'Placement' || type === 'Vacation') {
    let y = 170;

    pdf.text('Designation', 25, 161);
    pdf.text('Quantité', 68, 161);
    pdf.text('PU (HT)', 110, 161);
    pdf.text('Montant (HT)', 160, 161);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.setDrawColor(0, 0, 0);

    for (let idx = 0; idx < quotation.products.length; idx++) {
      const p = quotation.products[idx];
      pdf.text(`${p.product || ''} H/F`, 20, y);
      pdf.text(String(p.poste || ''), 20, y + 5);
      y += 5;

      const taux = p.taux !== '' ? parseFloat(p.taux) : 0;
      const coef = p.coefficient !== '' ? parseFloat(p.coefficient) : 0;

      if (p.taux !== '') pdf.text(taux.toFixed(2) + '€', 110, y);
      if (p.coefficient !== '') pdf.text(String(coef), 75, y);
      if (p.taux !== '' && p.coefficient !== '') pdf.text((taux * coef).toFixed(2) + '€', 175, y);

      y += 5;

      pdf.setTextColor(128, 128, 128);
      pdf.setDrawColor(128, 128, 128);
      if (idx === quotation.products.length - 1) {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
      }
      pdf.line(15, y, 195, y);
      pdf.setTextColor(0, 0, 0);
      pdf.setDrawColor(0, 0, 0);
      y += 7;

      if (y > 260) { pdf.addPage(); y = 10; }
    }

    if (y > 250) { pdf.addPage(); y = 10; }

    // Totaux (calculés une fois)
    const subtotal = quotation.products.reduce((total, item) => {
      const t = parseFloat(item.taux || 0);
      const c = parseFloat(item.coefficient || 0);
      return total + (t * c);
    }, 0);
    const tva = +(subtotal * 0.20).toFixed(2);
    const ttc = +(subtotal + tva).toFixed(2);

    y += 6;

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('Helvetica', 'normal');

    pdf.text('Total HT', 110, y);
    pdf.text(subtotal.toFixed(2) + ' €', 174, y);

    y += 10;
    pdf.text(tva.toFixed(2) + ' €', 174, y);

    y += 10;
    pdf.setFillColor(9, 132, 227);
    pdf.rect(105, y - 7, 90, 10, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('Helvetica', 'bold');
    pdf.text('Total TTC', 110, y);
    pdf.text(ttc.toFixed(2) + ' €', 174, y);

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('Helvetica', 'normal');
    y += 10;

    if (y >= 270) pdf.addPage();

    pdf.setFontSize(9);

    if (y > 280) { pdf.addPage(); y = 10; }

    const paymentCondition = splitStringWithoutCuttingWords(
      'Conditions de paiement : ' + String(quotation.products?.[0]?.comment || '').replace(/\n/g, ''),
      70
    );

    pdf.text('Nom et qualité du signataire :', 15, y);
    pdf.text('Date : ' + formatJsDate(new Date()), 15, y + 5);
    pdf.text('Moyen de règlement : Virement/Chèque/CB', 90, y);

    for (const line of paymentCondition) {
      pdf.text(line, 90, y + 5);
      y += 4;
      if (y >= 280) { pdf.addPage(); y = 10; }
    }

    if (type === 'Placement') {
      const replaceCondition = splitStringWithoutCuttingWords(
        'Conditions de remplacement : ' + String(quotation.products?.[0]?.comment2 || '').replace(/\n/g, ''),
        70
      );
      for (const line of replaceCondition) {
        pdf.text(line, 90, y + 10);
        y += 4;
        if (y >= 280) { pdf.addPage(); y = 10; }
      }
    }
  }

  // Pied de page coordonnées juridiques
  pdf.text(
    "J'm intérim - SAS au capital de 80 000 € - 4 Avenue Laurent Cély, Hall A, 92600 Asnières sur Seine, France",
    25,
    280
  );
  pdf.text('SIRET : 91912551800010 - Code APE : 7820z - N° de TVA : FR52919125518', 50, 285);
}

function addCGP(pdf, quotation, assets) {
  pdf.addPage();
  const cgp = pickCGP(quotation.agency?.name, assets);
  pdf.addImage(cgp, 'PNG', 10, 10, 198, 282, 'cgp');
}

// ========================= API =========================

// Génération (ouvre dans un nouvel onglet)
export const viewQuotationPDF = async (quotation) => {
  const assets = await warmupPdfAssets();

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  addHeader(pdf, quotation, assets);
  addIntroBoxes(pdf, quotation);
  addTableAndTotals(pdf, quotation);
  addCGP(pdf, quotation, assets);

  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // optionnel: setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Génération (télécharge)
export const downloadQuotationPDF = async (quotation) => {
  const assets = await warmupPdfAssets();

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  addHeader(pdf, quotation, assets);
  addIntroBoxes(pdf, quotation);
  addTableAndTotals(pdf, quotation);
  addCGP(pdf, quotation, assets);

  const filename = `devis_${quotation.quotationNumber || 'provisoire'}_${formatJsDate2(quotation.createdAt)}.pdf`;
  pdf.save(filename);
};

// Pour compat compat (alias de view)
export const generatePDF = async (quotation) => {
  await viewQuotationPDF(quotation);
};
